<template>
  <div>
    <!-- disabled button to show for unauthenticated/unauthorized users -->
    <template v-if="actionPermissions.block">
      <button
        id="saveToLibraryButton"
        :class="compact ? 'btn btn-success-compact' : 'btn btn-success btn-lg btn-block'"
        @click="() => {showBlocker = true}">
        <i :class="compact ? 'fa fa-folder' : 'fa fa-save'"/> Save to {{ compact ? 'Folder' : 'Library' }}
      </button>
      <LimitActionModal :show.sync="showBlocker" :action-permissions="actionPermissions" />
    </template>

    <!-- saved state button for compact mode -->
    <div
      v-if="!actionPermissions.block && compact && recentlySavedFolder"
      class="btn-saved-state"
    >
      <span>Saved to</span>
      <span class="underline">
        <i class="fa fa-folder"/>
        <a :href="libraryFolder(recentlySavedFolder.uuid, betaVisibility)">{{ recentlySavedFolder.name }}</a>
      </span>
    </div>

    <!-- button dropdown for regular, active users -->
    <A11yDropdown
      v-if="!actionPermissions.block && !(compact && recentlySavedFolder)"
      :dropdown-props="{
        ref:'dropdown',
        class: compact ? 'library-dropdown' : 'library-dropdown btn-block'
      }"
      :button-props="{
        'aria-live':'polite',
        'data-role':'trigger',
        class: compact ? 'btn btn-success-compact dropdown-toggle' : 'btn btn-success btn-lg btn-block dropdown-toggle',
        disabled:saving,
        id:'saveToLibraryButton',
        'aria-labelledby':'save-to-library-label',
      }"
      :dom-attributes="{
        role:'menu',
      }"
    >
      <template slot="button-contents">
        <template v-if="!saving">
          <i :class="compact ? 'fa fa-folder' : 'fa fa-save'"/>
          <span id="save-to-library-label" aria-hidden="true">Save to {{ compact ? 'Folder' : 'Library' }}</span>
        </template>
        <template v-if="saving">
          <i class="fa fa-circle-o-notch fa-spin"/>
          <span>Saving...</span>
        </template>
        <span class="caret"/>
      </template>
      <template slot="dropdown-items">
        <li v-if="recentStudents.length > 0" :class="compact ? 'compact-dropdown-item' : ''"><a role="button" @click="saveForStudent(recentStudents[0])">Save to <span class="fs-block">{{ recentStudents[0].name }}</span></a></li>
        <li :class="compact ? 'compact-dropdown-item' : ''"><a role="button" @click="toggleStudentModal(true)">Save to {{ recentStudents.length > 0 && 'another' || 'a' }} folder</a></li>
      </template>
    </A11yDropdown>

    <div class="saved-count gb-text-1" v-if="libraryCount > 0">
      <span class="saved-count__count gb-text-3">{{ libraryCount }}</span> {{ libraryCount === 1 && 'version' || 'versions' }} of this page {{ libraryCount === 1 && 'is' || 'are' }} <br>saved in <a :href="libraryAllFolders">Your Library</a>.
    </div>

    <!-- modal where user selects student to save content for -->
    <modal
      v-model="showStudentModal"
      :title="modalTitle"
      @hide="choseStudent(null)">
      <transition name="fade">
        <ChooseStudentForm
          v-if="showStudentModal"
          :recent-students="recentStudents"
          :preselected-student="student"
          @selectedStudent:existing="choseStudent"
          @selectedStudent:new="choseStudent"
          @tabChange="handleTabChange"
          :content-type="contentType"
        />
      </transition>

      <div v-if="enableNotes">
        <hr>
        <h5>
          Add Notes <span class="text-muted font-weight-normal">You can add/change this later</span>
        </h5>
        <textarea
          rows="3"
          class="form-control"
          placeholder="How would you implement this strategy?"
          v-model="notes"/>
      </div>

      <div class="btn-toolbar" slot="footer">
        <button
          class="btn btn-link btn-lg"
          @click="toggleStudentModal(false)"
          type="button">Cancel</button>
        <button
          class="btn btn-success btn-lg"
          :disabled="!studentName || saving"
          @click="save"
          type="button">
          <template v-if="!saving">
            <span
              v-if="isSharedWithMeTabActive"
              class="material-icons send-icon"
              aria-hidden="true">send
            </span>
            {{ saveFolderButtonPrefix }}
            {{ truncatedStudentName || 'folder' }}
          </template>
          <template v-if="saving">Saving...</template>
        </button>
      </div>
    </modal>
  </div>
</template>

<script>
import { libraryQuotasMixin } from '@/components/library/library_quotas_mixin'
import LimitActionModal from '@/components/LimitActionModal'
import ChooseStudentForm from '@/components/library/ChooseStudentForm'
import { mapActions, mapState, mapGetters } from 'vuex'
import { CONTENT_TYPES } from '@/util/access_visibility'
import {
  addGoalForStudent,
  addStrategyForStudent,
  getLibraryCountForGoal,
  getLibraryCountForStrategy,
  addStudent,
  postStudentContent,
  addIODStatementForStudent
} from '@/api/library'
import { postPresentLevelStudent } from '@/api/presentlevels'
import { toolkitUrls } from '@/links'
import A11yDropdown from '@/components/A11yDropdown'

export default {
  name: 'SaveToLibraryButton',
  mixins: [libraryQuotasMixin],
  components: {
    A11yDropdown,
    LimitActionModal,
    ChooseStudentForm
  },
  data() {
    return {
      showStudentModal: false,
      fetchingStudents: false,
      saving: false,
      showAllStudents: false,
      showBlocker: false,
      student: null,
      libraryCount: 0,
      fetchingLibraryCount: false,
      libraryUrl: toolkitUrls.library,
      libraryAllFolders: toolkitUrls.libraryAllFolders,
      libraryFolder: toolkitUrls.libraryFolder,
      notes: '',
      isSharedWithMeTabActive: false,
      recentlySavedFolder: null
    }
  },
  props: {
    contentUuid: {
      type: String,
      default: ''
    },
    userUuid: {
      type: String,
      default: ''
    },
    actionPermissions: {
      type: Object,
      required: true
    },
    contentPayload: {
      type: Object,
      required: true
    },
    enableNotes: {
      type: Boolean,
      required: false,
      default: false
    },
    compact: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    ...mapState('library', ['students', 'recentStudents']),
    ...mapGetters('library', ['getUserRoleForFolder']),
    ...mapGetters('visibility', ['betaVisibility']),
    studentName() {
      return (this.student && this.student.name) || ''
    },
    truncatedStudentName() {
      return this.studentName.length > 20 ? this.studentName.substring(0, 20) + '...' : this.studentName
    },
    blockerText() {
      if (this.actionPermissions.blockUnauthenticated) {
        return 'You must be signed in to use this feature'
      }
      if (this.actionPermissions.blockExpired) {
        return 'You must have an active membership to use this feature'
      }
    },
    contentType() {
      return this.actionPermissions.contentType
    },
    modalTitle() {
      switch (this.contentType) {
        case CONTENT_TYPES.GOAL:
        case CONTENT_TYPES.SUPER_PAGE:
          return 'Save Goal Page to Library'
        case CONTENT_TYPES.STRATEGY:
          return 'Save Strategy Page to Library'
        case CONTENT_TYPES.PRESENTLEVEL:
          return 'Save Present Levels Statement to Library'
        default:
          return 'Save to Library'
      }
    },
    saveFolderButtonPrefix() {
      return this.isSharedWithMeTabActive ? 'Contribute to' : 'Save to';
    }
  },
  methods: {
    ...mapActions('library', ['fetchStudents', 'fetchRecentStudents']),
    toggleStudentModal(flag) {
      this.showStudentModal = flag
      this.notes = ''
    },
    choseStudent(student) {
      this.student = student
    },
    fetchLibraryCount() {
      if (this.userUuid && this.contentUuid && !this.fetchingLibraryCount) {
        if ([CONTENT_TYPES.GOAL, CONTENT_TYPES.SUPER_PAGE].indexOf(this.contentType) > -1) {
          getLibraryCountForGoal(this.userUuid, this.contentUuid).then(count => {
            this.libraryCount = count
            this.fetchingLibraryCount = false
          })
        } else if (this.contentType === CONTENT_TYPES.STRATEGY) {
          getLibraryCountForStrategy(this.userUuid, this.contentUuid).then(data => {
            this.libraryCount = data.length
            this.fetchingLibraryCount = false
          })
        }
      }
    },
    loadUserStudents() {
      if (!this.actionPermissions.block) {
        this.fetchingStudents = true
        this.fetchRecentStudents({ userId: this.userUuid })
          .then(() => {
            return this.fetchStudents(this.userUuid)
          })
          .then(() => {
            this.fetchingStudents = false
          })
          .catch(err => {
            console.error(`Error loading folders in SaveToLibraryButton: ${err}`)
          })
      }
    },
    saveForStudent(student) {
      this.student = student
      if (this.enableNotes) {
        this.toggleStudentModal(true)
      } else {
        this.save()
      }
    },
    handleIODSave() {
      const promise = addIODStatementForStudent({
        userUuid: this.userUuid,
        studentUuid: this.student.uuid,
        studentName: this.student.name,
        statement: this.contentPayload.statement,
        impactAreas: this.contentPayload.impactAreas,
        disability: this.contentPayload.disability,
        subjects: this.contentPayload.subjects
      })
      this.$sendEvent(
        'Clicked Save Impact of Disability to Library',
        {
          user_folder_role: this.student?.uuid ? this.getUserRoleForFolder(this.student.uuid) : undefined
        },
        {
          cu: this.contentUuid,
          ct: this.actionPermissions.contentTitle
        }
      )
      return promise
    },
    save() {
      if (
        this.student &&
        this.student.uuid &&
        this.maxContentReachedForStudent(this.contentType, this.student.uuid)
      ) {
        this.sendMaxContentInFolderEvent(this.contentType, this.student.uuid)
        this.showStudentModal = false
        this.$emit('saved:error', this.maxContentTypeErrorMessage(this.contentType))
        return
      }
      this.saving = true
      this.$emit('saved:start')
      var promise
      if ([CONTENT_TYPES.GOAL, CONTENT_TYPES.SUPER_PAGE].indexOf(this.contentType) > -1) {
        promise = addGoalForStudent(this.student, this.contentPayload)
        // analytics tracking
        this.$sendEvent(
          'Clicked Save Goal to Library',
          {
            user_folder_role: this.student?.uuid ? this.getUserRoleForFolder(this.student.uuid) : undefined
          },
          {
            cu: this.contentUuid,
            ct: this.actionPermissions.contentTitle
          }
        )
      } else if ([CONTENT_TYPES.PRESENTLEVEL].indexOf(this.contentType) > -1) {
        var studentUuid = this.student.uuid
        let newStudent = !studentUuid
        if (newStudent) {
          promise = addStudent(this.student.name, this.userUuid).then(data => {
            studentUuid = data.uuid
          })
        } else {
          promise = new Promise(res => {
            res()
          })
        }

        promise = promise
          // postPresentLevelStudent must occur before postStudentContent
          // because when the save operation completes, this component emits an event
          // where the expected payload is the result of adding the content to a collection
          .then(() => {
            return postPresentLevelStudent({
              uuid: this.contentUuid,
              userUuid: this.userUuid,
              studentUuid: studentUuid,
              pronoun: null,
              enrolledGrade: null,
              subjects: null
            })
          })
          .then(() => {
            return postStudentContent(
              studentUuid,
              CONTENT_TYPES.PRESENTLEVEL,
              this.contentUuid,
              this.userUuid            )
          })

        this.$sendEvent('Clicked Save Present Levels to Library', {
          plw_uuid: this.contentUuid,
          plw_new_student: newStudent,
          user_folder_role: this.getUserRoleForFolder(studentUuid),
          //Deprecated
          present_level_uuid: this.contentUuid
        })
      } else if (this.contentType === CONTENT_TYPES.STRATEGY) {
        promise = addStrategyForStudent({
          userUuid: this.userUuid,
          strategyUuid: this.contentUuid,
          studentUuid: this.student.uuid,
          studentName: this.student.name,
          notes: this.notes
        })
        this.$sendEvent(
          'Clicked Save Strategy to Library',
          {
            user_folder_role: this.student?.uuid ? this.getUserRoleForFolder(this.student.uuid) : undefined
          },
          {
            cu: this.contentUuid,
            ct: this.actionPermissions.contentTitle
          }
        )
      } else if (this.contentType === CONTENT_TYPES.IMPACT_OF_DISABILITY) {
        promise = this.handleIODSave()
      }

      if (promise) {
        promise
          .then(resp => {
            this.saving = false
            this.showStudentModal = false
            // Track the folder that was just saved to for compact mode
            if (this.compact && this.student) {
              this.recentlySavedFolder = this.student
            }
            this.student = null
            this.fetchLibraryCount()
            this.loadUserStudents()
            this.$emit('saved:success', resp)
          })
          .catch(err => {
            this.saving = false
            this.showStudentModal = false
            this.$emit('saved:error', err.message)
          })
      }
    },
    handleTabChange({ isSharedTab }) {
      this.isSharedWithMeTabActive = isSharedTab
      this.student = null
    }
  },
  watch: {
    userUuid(newVal) {
      if (newVal) {
        this.loadUserStudents()
        this.fetchLibraryCount()
      }
    },
    actionPermissions(newPermissions, oldPermissions) {
      if (oldPermissions.block && !newPermissions.block) {
        this.loadUserStudents()
      }
    },
    contentUuid(newVal) {
      // reset content-specific data/state
      this.saving = false
      this.student = null
      this.showStudentModal = false
      this.libraryCount = 0
      this.recentlySavedFolder = null
      this.fetchLibraryCount()
    }
  },
  mounted() {
    if (this.userUuid) {
      this.loadUserStudents()
      this.fetchLibraryCount()
    }
  }
}
</script>

<style lang="less">
@import '~@/variables.less';
#saveToLibraryButton {
  &:hover, &:focus {
    background-color: var(--green-button-hover);
  }
  &:focus {
    outline: solid 2px var(--green-button-hover);
    outline-offset: 2px;
  }
}

.btn-success-compact {
  background-color: var(--green-default);
  color: #ffffff;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  line-height: 16px;
  font-family: 'Helvetica Neue', sans-serif;
  font-weight: 400;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  cursor: pointer;

  &:hover, &:focus {
    background-color: darken(#136320, 5%);
    color: #ffffff;
  }

  &:focus {
    outline: solid 2px darken(#136320, 5%);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  i {
    font-size: 16px;
    padding-left: 4px;
    padding-right: 2px;
  }

  .caret {
    font-size: 8px;
    margin-left: 4px;
    margin-right: 4px;
  }
}

.btn-saved-state {
  background-color: transparent;
  cursor: default;
  gap: 4px;
  color: var(--green-default);
  opacity: 1 !important;

  &:hover, &:focus {
    background-color: transparent !important;
    color: var(--green-default);
  }

  a {
    color: var(--green-default);
  }

  i {
    font-size: 14px;
    padding-left: 0;
    padding-right: 0;
  }
}
.underline, .underline i {
  text-decoration: underline;
}

.library-dropdown {
  .dropdown-menu {
    width: 100%;
    li a {
      white-space: normal;
    }
    .compact-dropdown-item a:hover {
      background-color: var(--gray-border-weak);
      color: var(--gray-default);
    }
  }
}
.saved-count {
  margin-top: 12px;
  text-align: center;
  color: @gray-light;
  .saved-count__count {
    font-weight: bold;
  }
}

.font-weight-normal {
  font-weight: normal;
}

.btn-block {
  min-width: fit-content;
}

.send-icon {
  vertical-align: middle;
  margin-right: 8px;
}
</style>
<style lang="less" scoped>
.btn-toolbar .btn {
  float: none;
}
.btn-link {
  border: 1px solid #CCC;
  color: #333;
  &:hover {
    text-decoration: none;
    border: 1px solid #CCC;
    background: #E6E6E6;
    color: #333;
  }
}
</style>
